import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link as RouterLink, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const isCompact = scrolled;

  const leftMenuItems = [{ label: 'Corps', href: '#about' }];
  const rightMenuItems = [{ label: 'Kalender', href: '#veranstaltungen' }];
  const allMenuItems = [...leftMenuItems, ...rightMenuItems];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const scrollToSection = (href) => {
    if (location.pathname !== '/') {
      window.location.href = '/' + href;
      return;
    }

    const element = document.querySelector(href);
    if (element) {
      const offset = element.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: offset, behavior: 'smooth' });
    }

    if (isMobile) setMobileOpen(false);
  };

  const navButtonStyle = {
    color: '#111',
    fontWeight: 400,
    fontSize: '0.95rem',
    textTransform: 'none',
    letterSpacing: '0.08em',
    position: 'relative',
    px: 1,
    transition: '0.3s ease',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      bottom: 0,
      width: 0,
      height: '2px',
      bgcolor: '#3a7b3e',
      transition: '0.3s ease',
    },
    '&:hover::after': {
      width: '100%',
    },
    '&:hover': {
      bgcolor: 'transparent',
      color: '#3a7b3e',
    },
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: isCompact
            ? 'rgba(255,255,255,0.92)'
            : 'rgba(255,255,255,0.75)',
          backdropFilter: 'blur(14px)',
          borderBottom: isCompact
            ? '1px solid rgba(0,0,0,0.06)'
            : '1px solid transparent',
          boxShadow: isCompact
            ? '0 2px 10px rgba(0,0,0,0.04)'
            : 'none',
          transition: '0.35s ease',
        }}
      >
        <Toolbar
          sx={{
            maxWidth: '1200px',
            width: '100%',
            mx: 'auto',

            // 🔥 Premium spacing oben im Top-State
            pt: isCompact ? 1 : 3,
            pb: isCompact ? 1 : 2,

            transition: '0.35s ease',

            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {/* MOBILE */}
          {isMobile ? (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box component={RouterLink} to="/">
                <Box
                  component="img"
                  src="/Corps_Guestphalia_Berlin_Logo_01.png"
                  alt="Logo"
                  sx={{
                    height: isCompact ? 70 : 95,
                    transition: '0.35s ease',
                  }}
                />
              </Box>

              <IconButton onClick={handleDrawerToggle}>
                <MenuIcon />
              </IconButton>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',

                // 🔥 oben enger, unten weiter auseinander
                gap: isCompact ? 6 : 2,
                transition: '0.35s ease',
              }}
            >
              {/* LINKS */}
              <Box sx={{ display: 'flex', gap: isCompact ? 2 : 3 }}>
                {leftMenuItems.map((item) => (
                  <Button
                    key={item.label}
                    onClick={() => scrollToSection(item.href)}
                    sx={navButtonStyle}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>

              {/* LOGO (Hero Focus) */}
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  mx: isCompact ? 2 : 6,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box
                  component="img"
                  src="/Corps_Guestphalia_Berlin_Logo_01.png"
                  alt="Logo"
                  sx={{
                    height: isCompact ? 85 : 150,
                    transition: '0.35s ease',
                    transform: isCompact ? 'translateY(0)' : 'translateY(6px)',
                  }}
                />
              </Box>

              {/* RECHTS */}
              <Box sx={{ display: 'flex', gap: isCompact ? 2 : 3 }}>
                {rightMenuItems.map((item) => (
                  <Button
                    key={item.label}
                    onClick={() => scrollToSection(item.href)}
                    sx={navButtonStyle}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* MOBILE DRAWER */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
      >
        <Box sx={{ width: 280, p: 2 }}>
          <List>
            {allMenuItems.map((item) => (
              <ListItem key={item.label} disablePadding>
                <ListItemButton
                  onClick={() => scrollToSection(item.href)}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    '&:hover': {
                      bgcolor: 'rgba(58,123,62,0.08)',
                    },
                  }}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;